/**
 * Consentimientos Hooks - TanStack Query hooks for consent management
 *
 * Flujo: descargar plantilla .docx → imprimir → firma física → escanear → subir PDF/imagen
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getConsentimientos,
  getConsentimiento,
  createConsentimiento,
  deleteConsentimiento,
  deactivateConsentimiento,
  activateConsentimiento,
  getTiposRequeridos,
  type Consentimiento,
  type ConsentimientoListItem,
  type ConsentimientoCreateData,
  type ConsentimientoFilters,
  type PaginatedResponse,
} from "@/services/consentimientos";

// Re-export types
export type {
  Consentimiento,
  ConsentimientoListItem,
  ConsentimientoCreateData,
  ConsentimientoFilters,
  TipoConsentimiento,
  RelacionFirmante,
} from "@/services/consentimientos";

export {
  TIPOS_CONSENTIMIENTO_VALUES,
  RELACIONES_FIRMANTE_VALUES,
  descargarPlantilla,
  descargarDocumento,
} from "@/services/consentimientos";

// Query keys
export const consentimientoKeys = {
  all: ["consentimientos"] as const,
  lists: () => [...consentimientoKeys.all, "list"] as const,
  list: (filters: ConsentimientoFilters) => [...consentimientoKeys.lists(), filters] as const,
  details: () => [...consentimientoKeys.all, "detail"] as const,
  detail: (id: number) => [...consentimientoKeys.details(), id] as const,
  tiposRequeridos: (codigo: string) =>
    [...consentimientoKeys.all, "tipos-requeridos", codigo] as const,
};

/**
 * Hook to fetch paginated consentimientos
 */
export function useConsentimientos(filters: ConsentimientoFilters = {}) {
  return useQuery<PaginatedResponse<ConsentimientoListItem>>({
    queryKey: consentimientoKeys.list(filters),
    queryFn: () => getConsentimientos(filters),
  });
}

/**
 * Hook to fetch a single consentimiento
 */
export function useConsentimiento(id: number | null) {
  return useQuery<Consentimiento>({
    queryKey: consentimientoKeys.detail(id!),
    queryFn: () => getConsentimiento(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch required consent types for an exam
 */
export function useTiposRequeridos(codigoExamen: string) {
  return useQuery({
    queryKey: consentimientoKeys.tiposRequeridos(codigoExamen),
    queryFn: () => getTiposRequeridos(codigoExamen),
    enabled: !!codigoExamen,
  });
}

/**
 * Hook to create a consentimiento
 */
export function useCreateConsentimiento(options?: {
  onSuccess?: (data: Consentimiento) => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ConsentimientoCreateData) => createConsentimiento(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: consentimientoKeys.lists() });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

/**
 * Hook to delete a consentimiento (hard delete)
 */
export function useDeleteConsentimiento(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteConsentimiento(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: consentimientoKeys.lists() });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

/**
 * Hook to deactivate a consentimiento (soft delete)
 */
export function useDeactivateConsentimiento(options?: {
  onSuccess?: (data: Consentimiento) => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deactivateConsentimiento(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: consentimientoKeys.lists() });
      queryClient.invalidateQueries({ queryKey: consentimientoKeys.detail(data.id) });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

/**
 * Hook to activate a consentimiento
 */
export function useActivateConsentimiento(options?: {
  onSuccess?: (data: Consentimiento) => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => activateConsentimiento(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: consentimientoKeys.lists() });
      queryClient.invalidateQueries({ queryKey: consentimientoKeys.detail(data.id) });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}
